import { find, isString, map } from "lodash";
import { Moment } from "moment-timezone";
import { Timezone } from "./timezone";

export enum Interval {
  Day = "day",
  Month = "month",
  Week = "week",
  Year = "year",
  Hour = "hour",
  Minute = "minute",
  Quarter = "Q",
}

export type AbstractDate = Moment | Date | string | number | DateMath;

export interface Range {
  from: AbstractDate;
  to: AbstractDate;
  interval: Interval;
}

export type PeriodRange = Omit<Range, "interval">;

type PeriodMathRange = {
  label: PeriodId;
  from: AbstractDate;
  to: AbstractDate;
};

export enum DateMath {
  Last_24_Hours = "now-23h/h",
  Last_7_Days = "now-6d/d",
  Last_14_Days = "now-13d/d",
  Last_30_Days = "now-29d/d",
  Last_60_Days = "now-59d/d",
  Last_90_Days = "now-89d/d",
  Last_365_Days = "now-364d/d",
  Last_730_Days = "now-729d/d",
  Today = "now/d",
  Yesterday = "now-1d/d",
  Current_Week = "now/w",
  Last_Week = "now-1w/w",
  Current_Month = "now/M",
  Last_Month = "now-1M/M",
  Current_Quarter = "now/q",
  Last_Quarter = "now-1q/q",
  Current_Year = "now/y",
  Last_Year = "now-1y/y",
  Now = "now",
}

export enum PeriodId {
  Last_24_Hours = "Last_24_Hours",
  Last_7_Days = "Last_7_Days",
  Last_14_Days = "Last_14_Days",
  Last_30_Days = "Last_30_Days",
  Last_60_Days = "Last_60_Days",
  Last_90_Days = "Last_90_Days",
  Last_365_Days = "Last_365_Days",
  Last_730_Days = "Last_730_Days",
  Today = "Today",
  Yesterday = "Yesterday",
  Current_Week = "Current_Week",
  Current_Month = "Current_Month",
  Current_Quarter = "Current_Quarter",
  Current_Year = "Current_Year",
  Last_Week = "Last_Week",
  Last_Month = "Last_Month",
  Last_Quarter = "Last_Quarter",
  Last_Year = "Last_Year",
  Custom = "Custom",
  Whole_Period = "Whole_Period",
  Custom_From = "Custom_From",
}

export enum PeriodType {
  Rolling = "rolling",
  Classic = "classic",
  From = "from",
  Custom = "custom",
  Milestones = "milestones",
}

export enum Panels {
  Calendar = "calendar",
  CalendarFrom = "calendar-from",
  Milestones = "milestones",
  Rolling = "rolling",
}

export interface Period {
  from: DateMath;
  to: DateMath;
  tz?: Timezone;
  id: PeriodId | string;
  type?: PeriodType;
}

export type AbstractPeriod = Period | PeriodId;

export const PeriodMath: Record<
  Exclude<PeriodId, "Custom" | "Custom_From" | "Whole_Period">,
  Period
> = {
  // Rolling dates
  [PeriodId.Last_24_Hours]: {
    from: DateMath.Last_24_Hours,
    to: DateMath.Now,
    id: PeriodId.Last_24_Hours,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_7_Days]: {
    from: DateMath.Last_7_Days,
    to: DateMath.Now,
    id: PeriodId.Last_7_Days,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_14_Days]: {
    from: DateMath.Last_14_Days,
    to: DateMath.Now,
    id: PeriodId.Last_14_Days,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_30_Days]: {
    from: DateMath.Last_30_Days,
    to: DateMath.Now,
    id: PeriodId.Last_30_Days,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_60_Days]: {
    from: DateMath.Last_60_Days,
    to: DateMath.Now,
    id: PeriodId.Last_60_Days,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_90_Days]: {
    from: DateMath.Last_90_Days,
    to: DateMath.Now,
    id: PeriodId.Last_90_Days,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_365_Days]: {
    from: DateMath.Last_365_Days,
    to: DateMath.Now,
    id: PeriodId.Last_365_Days,
    type: PeriodType.Rolling,
  },
  [PeriodId.Last_730_Days]: {
    from: DateMath.Last_730_Days,
    to: DateMath.Now,
    id: PeriodId.Last_730_Days,
    type: PeriodType.Rolling,
  },
  // Current
  [PeriodId.Today]: {
    from: DateMath.Today,
    to: DateMath.Now,
    id: PeriodId.Today,
    type: PeriodType.Classic,
  },
  [PeriodId.Yesterday]: {
    from: DateMath.Yesterday,
    to: DateMath.Today,
    id: PeriodId.Yesterday,
    type: PeriodType.Classic,
  },
  [PeriodId.Current_Week]: {
    from: DateMath.Current_Week,
    to: DateMath.Now,
    id: PeriodId.Current_Week,
    type: PeriodType.Classic,
  },
  [PeriodId.Last_Week]: {
    from: DateMath.Last_Week,
    to: DateMath.Current_Week,
    id: PeriodId.Last_Week,
    type: PeriodType.Classic,
  },
  [PeriodId.Current_Month]: {
    from: DateMath.Current_Month,
    to: DateMath.Now,
    id: PeriodId.Current_Month,
    type: PeriodType.Classic,
  },
  [PeriodId.Last_Month]: {
    from: DateMath.Last_Month,
    to: DateMath.Current_Month,
    id: PeriodId.Last_Month,
    type: PeriodType.Classic,
  },
  [PeriodId.Current_Quarter]: {
    from: DateMath.Current_Quarter,
    to: DateMath.Now,
    id: PeriodId.Current_Quarter,
    type: PeriodType.Classic,
  },
  [PeriodId.Last_Quarter]: {
    from: DateMath.Last_Quarter,
    to: DateMath.Current_Quarter,
    id: PeriodId.Last_Quarter,
    type: PeriodType.Classic,
  },
  [PeriodId.Current_Year]: {
    from: DateMath.Current_Year,
    to: DateMath.Now,
    id: PeriodId.Current_Year,
    type: PeriodType.Classic,
  },
  [PeriodId.Last_Year]: {
    from: DateMath.Last_Year,
    to: DateMath.Current_Year,
    id: PeriodId.Last_Year,
    type: PeriodType.Classic,
  },
};

const generatePeriodsMathRange = (): PeriodMathRange[] => {
  return map(PeriodMath, ({ from, to }, label) => ({
    label: label as PeriodId,
    from,
    to,
  }));
};

const getIdFromPeriodMatch = (
  to: AbstractDate,
  period?: PeriodMathRange
): PeriodId => {
  if (period) return period.label;
  if (to === DateMath.Now) return PeriodId.Custom_From;
  return PeriodId.Custom;
};

export function getPeriodId(
  from: AbstractDate,
  to: AbstractDate = DateMath.Now
): PeriodId {
  if (isString(from) && isString(to)) {
    const periods = generatePeriodsMathRange();
    const periodMatch = find(periods, { from, to });
    return getIdFromPeriodMatch(to, periodMatch);
  }
  return PeriodId.Custom;
}

export function getRange(
  from: AbstractDate,
  to: AbstractDate,
  interval: Interval = Interval.Day
): Range {
  return {
    from,
    to,
    interval,
  };
}
